import cn from 'classnames'
import BaseModal, {
  BaseModalProps,
} from 'ext-ui/components/BaseModal/BaseModal'
import React, { useEffect } from 'react'
import { useSelector } from 'react-redux'
import { useHistory } from 'react-router-dom'
import useAnalyticsContext from 'src/analytics/useAnalyticsContext'
import LockedNew from 'src/assets/images/LockedNew.svg'
import PaymentGateBanner from 'src/assets/images/PaymentGateBanner.svg'
import Button, {
  ButtonDesigns,
  ButtonTypes,
} from 'src/components/Button/Button'
import HoverableLink from 'src/components/HoverableLink/HoverableLink'
import Trns from 'src/localization/Trns'
import { upgradeSubscriptionUrl } from 'src/navigation/urls'
import { usePostEnterpriseRequest, usePutManageSubscription } from 'src/queries/organizations/saas.queries'
import { selectUserExplicitOrganizationListModelFromState } from 'src/redux/selectors/data/organizations.selectors'

import style from './SaasActionRequiredModal.style'

export enum SaasActionRequiredType {
  PAYMENT_REQUIRED = 'PAYMENT_REQUIRED',
  DOWNGRADE_ACTION_REQUIRED = 'DOWNGRADE_ACTION_REQUIRED',
}

enum UpgradeOption {
  PRO = 'PRO',
  ENTERPRISE = 'ENTERPRISE',
  MANAGE_SUBSCRIPTION = 'MANAGE_SUBSCRIPTION',
}

type SaasActionRequiredProps = BaseModalProps & {
  exceptionType?: SaasActionRequiredType
  error?: string
  upgradeOptions?: UpgradeOption[]
  header?: string
  footer?: string
  buttonText?: string
  analytics?: {
    event: string
    properties: Record<string, string>
  }
}

const SaasActionRequiredModal: React.FC<SaasActionRequiredProps> = ({
  isOpen,
  onClose,
  className,
  ...saasActionRequiredPopupProps
}): React.ReactElement => {

  const history = useHistory()

  const { mutate: postEnterpriseRequest } = usePostEnterpriseRequest()
  const { mutate: putManageSubscription } = usePutManageSubscription()

  const orgId = useSelector(
    selectUserExplicitOrganizationListModelFromState,
  )?.id
  const { analytics } = useAnalyticsContext()

  useEffect(() => {
    if (saasActionRequiredPopupProps?.analytics) {
      analytics.track(
        saasActionRequiredPopupProps.analytics.event,
        saasActionRequiredPopupProps.analytics.properties,
      )
    }
  }, [analytics, saasActionRequiredPopupProps?.analytics])

  return (
    <BaseModal
      isOpen={isOpen}
      onClose={onClose}
      variant="interactive"
      className={cn('SaasActionRequiredModal', className)}
      css={style}
    >
      <img src={PaymentGateBanner} alt={''} />
      <div className="SaasActionRequiredModal-Text">
        <header>{saasActionRequiredPopupProps?.header}</header>
        <p>{saasActionRequiredPopupProps?.error}</p>
      </div>
      <div className="SaasActionRequiredModal-CTA">
        {saasActionRequiredPopupProps?.buttonText && (
          <Button
            testId={'saasUpgradeSubmit'}
            design={ButtonDesigns.designCta}
            type={ButtonTypes.button}
            text={saasActionRequiredPopupProps?.buttonText}
            className={'SaasActionRequiredModal-CTA-Button'}
            onClick={(): void => {
              if (saasActionRequiredPopupProps.upgradeOptions?.includes(UpgradeOption.ENTERPRISE)) {
                postEnterpriseRequest({
                  orgId: orgId || '',
                  body: {
                    intention:
                      saasActionRequiredPopupProps?.header ||
                      'Unknown Enterprise Pay Gate',
                  },
                })
              } else if (saasActionRequiredPopupProps.upgradeOptions?.includes(UpgradeOption.MANAGE_SUBSCRIPTION)) {
                putManageSubscription({
                    orgId: orgId || '',
                    redirect: true,
                    body: {}
                  })
              } else {
                history.push(upgradeSubscriptionUrl())
              }
              onClose()
            }}
          />
        )}
        { saasActionRequiredPopupProps.exceptionType === SaasActionRequiredType.PAYMENT_REQUIRED && (
          <HoverableLink
            href={
              'https://support.paywithextend.com/hc/en-us/articles/32069180604951-How-do-I-upgrade-my-tier'
            }
            ariaLabelSource={saasActionRequiredPopupProps?.header || ''}
          >
            <Trns source={'Learn more'} />
          </HoverableLink>
        )}
      </div>
      {saasActionRequiredPopupProps?.footer && (
        <div className={'SaasActionRequiredModal-Footer'}>
          <img src={LockedNew} alt={''} />
          <span>{saasActionRequiredPopupProps?.footer}</span>
        </div>
      )}
    </BaseModal>
  )
}
export default SaasActionRequiredModal
